import { useTranslation, type I18nKey } from '@pos/i18n';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useAdmin } from '../adminContext';
import { useFormat } from '../orders/model';
import { asProblem, problemMessage } from '../problems';
import { ListTable } from './ListTable';

/** One audit entry as the audit log API returns it (ADR 0118). */
interface AuditEntry {
  id: string;
  occurredAt: string;
  staffCode?: string | null;
  userName?: string | null;
  actionCode: string;
  targetType?: string | null;
  details?: { label?: string; fields?: Record<string, unknown[]> } | null;
}

interface LooseClient {
  GET(path: string, init?: object): Promise<{ data?: unknown; error?: unknown }>;
}

const CHANGE_ACTIONS = ['change.create', 'change.update', 'change.delete', 'change.restore'] as const;

function text(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? '✓' : '✗';
  return typeof value === 'object' ? JSON.stringify(value) : String(value);
}

/**
 * Audit entries as a table (ADR 0118): when, who, what and the fields changed, before → after, newest first, paged on the server.
 * Personal data shows as "•••": the log records that it changed, not what it was. `targetType` and `targetId` narrow it to one
 * record (its History tab); without them it is a whole log with search and a column naming each record.
 */
export function AuditTrailTable({ targetType, targetId }: { targetType?: string; targetId?: string }) {
  const { t } = useTranslation();
  const format = useFormat();
  const { client, history, resources } = useAdmin();
  const [paging, setPaging] = useState({ page: 1, pageSize: 25 });
  const [search, setSearch] = useState('');
  const oneRecord = Boolean(targetId);

  const entries = useQuery({
    queryKey: ['admin', 'audit-trail', targetType, targetId, search, paging.page, paging.pageSize],
    queryFn: async () => {
      if (!history) throw new Error('No audit log configured');
      const { data, error } = await (client as LooseClient).GET(history.path, {
        params: { query: { targetType, targetId, search: search || undefined, page: paging.page, pageSize: paging.pageSize } },
      });
      if (!data) throw error;
      return data as { items: AuditEntry[]; total: number | string };
    },
    placeholderData: keepPreviousData,
    enabled: Boolean(history),
  });

  const field = (column: string) => t(`md.fields.${column}` as I18nKey, { defaultValue: column });
  const action = (code: string) =>
    (CHANGE_ACTIONS as readonly string[]).includes(code) ? t(`admin.history.actions.${code.slice('change.'.length)}` as I18nKey) : code;
  const record = (e: AuditEntry) => {
    const def = e.targetType ? resources[e.targetType] : undefined;
    const kind = def ? t(def.title) : (e.targetType ?? '—');
    return e.details?.label ? `${kind}: ${e.details.label}` : kind;
  };

  return (
    <ListTable<AuditEntry>
      ariaLabel={t('admin.history.title')}
      loading={entries.isLoading}
      error={entries.error ? problemMessage(t, asProblem(entries.error)) : undefined}
      onRetry={() => void entries.refetch()}
      rows={entries.data?.items ?? []}
      search={
        oneRecord
          ? undefined
          : {
              label: t('admin.search'),
              onChange: (value) => {
                setSearch(value);
                setPaging((p) => ({ ...p, page: 1 }));
              },
            }
      }
      columns={[
        { key: 'when', header: t('admin.history.when'), render: (e) => format.dateTime(e.occurredAt) },
        {
          key: 'who',
          header: t('admin.history.who'),
          render: (e) => (e.staffCode ? `${e.staffCode} · ${e.userName ?? ''}` : t('admin.history.system')),
        },
        { key: 'what', header: t('admin.history.what'), render: (e) => action(e.actionCode) },
        ...(oneRecord ? [] : [{ key: 'record', header: t('admin.history.record'), render: record }]),
        {
          key: 'changes',
          header: t('admin.history.changes'),
          render: (e) => {
            const fields = Object.entries(e.details?.fields ?? {});
            if (fields.length === 0) return '—';
            return (
              <ul className="pos-record-history__changes">
                {fields.map(([column, values]) => (
                  <li key={column}>
                    <strong>{field(column)}</strong>:{' '}
                    {values.length === 1 ? text(values[0]) : `${text(values[0])} → ${text(values[1])}`}
                  </li>
                ))}
              </ul>
            );
          },
        },
      ]}
      empty={{ heading: t('admin.history.emptyTitle'), description: t('admin.history.empty') }}
      pagination={{ ...paging, total: Number(entries.data?.total ?? 0), onChange: ({ page, pageSize }) => setPaging({ page, pageSize }) }}
    />
  );
}

/** A record's History tab (ADR 0118). */
export function RecordHistory({ targetType, targetId }: { targetType: string; targetId: string }) {
  return <AuditTrailTable targetType={targetType} targetId={targetId} />;
}
